import React, { useEffect, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  TextField,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import AppSelectField from "../../../lib/components/AppSelectField";
import AppTextField from "../../../lib/components/AppTextField";
import BTNLoader from "../../../lib/components/BTNLoader";
import marketplaceService from "../../../service/marketplaceService";
import organizationservice from "../../../service/organizationservice";
import { GRANT_ADDON_VALIDATION } from "../../../validations/marketplace";
import errorHandler from "../../../utils/errorHandler";
import useDebounce from "../../../hooks/useDebounce";

const defaultValues = {
  organizationId: "",
  itemCode: "",
  quantity: "",
  reason: "",
};

/**
 * B6 — Manually grant an add-on to an org for free (comped/support). Reason required.
 */
const GrantAddonModal = ({ isOpen, onClose, onSuccess, catalogItems = [] }) => {
  const formHook = useForm({
    resolver: yupResolver(GRANT_ADDON_VALIDATION),
    mode: "all",
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = formHook;

  const itemCode = useWatch({ control, name: "itemCode" });
  const [orgKeyword, setOrgKeyword] = React.useState("");
  const debouncedOrgKeyword = useDebounce(orgKeyword, 400);

  useEffect(() => {
    if (!isOpen) return;
    reset(defaultValues);
    setOrgKeyword("");
  }, [isOpen, reset]);

  const selectedItem = useMemo(
    () => catalogItems.find((i) => i.code === itemCode),
    [catalogItems, itemCode]
  );
  const isLimit = selectedItem?.kind === "LIMIT" || Boolean(selectedItem?.quantity);

  const orgsQuery = useQuery({
    retry: false,
    enabled: isOpen && Boolean(debouncedOrgKeyword),
    queryKey: ["marketplace-grant-orgs", debouncedOrgKeyword],
    queryFn: () =>
      organizationservice.get({ keyword: debouncedOrgKeyword, page: 1, limit: 20 }),
  });

  const orgOptions = useMemo(() => {
    const payload = orgsQuery.data?.data?.data ?? orgsQuery.data?.data ?? {};
    const list = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.docs)
        ? payload.docs
        : Array.isArray(payload?.organizations)
          ? payload.organizations
          : [];

    return list.map((org) => ({
      value: String(org.id || org._id),
      label: org.name || org.businessName || org.email || String(org.id || org._id),
    }));
  }, [orgsQuery.data]);

  const itemOptions = useMemo(
    () =>
      catalogItems.map((item) => ({
        value: item.code,
        label: `${item.name} (${item.code})`,
      })),
    [catalogItems]
  );

  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = {
        organizationId: values.organizationId,
        itemCode: values.itemCode,
        reason: values.reason,
      };
      if (isLimit && values.quantity) {
        payload.quantity = Number(values.quantity);
      }
      return marketplaceService.grantAddon(payload);
    },
    onError: ({ response }) => errorHandler(response),
    onSuccess: () => {
      toast.success("Add-on granted to organization");
      onSuccess?.();
      onClose?.();
    },
  });

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit((values) => mutation.mutate(values)),
      }}
    >
      <div className="d-flex justify-content-between align-items-center gap-3 p-3 pe-2">
        <div>
          <div className="fs-5 fw-medium">Grant Add-on</div>
          <div className="text-secondary fs-sm">
            Assign catalog access or capacity to a registered business
          </div>
        </div>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </div>
      <Divider />

      <DialogContent>
        <div className="mb-3">
          <label className="d-block mb-1">Search Organization</label>
          <AppTextField
            placeholder="Search by business name..."
            value={orgKeyword}
            onChange={(e) => setOrgKeyword(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="d-block mb-1">
            Organization <span className="text-danger">*</span>
          </label>
          <Controller
            name="organizationId"
            control={control}
            render={({ field }) => (
              <AppSelectField
                {...field}
                isLoading={orgsQuery.isFetching}
                items={orgOptions}
                placeholder={
                  debouncedOrgKeyword
                    ? "Select organization"
                    : "Search above to load organizations"
                }
                error={!!errors.organizationId}
                helperText={errors.organizationId?.message}
              />
            )}
          />
        </div>

        <div className="mb-3">
          <label className="d-block mb-1">
            Add-on <span className="text-danger">*</span>
          </label>
          <Controller
            name="itemCode"
            control={control}
            render={({ field }) => (
              <AppSelectField
                value={field.value}
                onChange={(e) => {
                  field.onChange(e);
                  setValue("quantity", "");
                }}
                items={itemOptions}
                placeholder="Select add-on"
                error={!!errors.itemCode}
                helperText={errors.itemCode?.message}
              />
            )}
          />
        </div>

        {isLimit ? (
          <div className="mb-3">
            <label className="d-block mb-1">Quantity</label>
            <Controller
              name="quantity"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  fullWidth
                  size="small"
                  placeholder="e.g. 100"
                  error={!!errors.quantity}
                  helperText={errors.quantity?.message}
                />
              )}
            />
          </div>
        ) : null}

        <div className="mb-1">
          <label className="d-block mb-1">
            Reason <span className="text-danger">*</span>
          </label>
          <Controller
            name="reason"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                minRows={2}
                placeholder="e.g. Testing phase 1 / Trial access"
                error={!!errors.reason}
                helperText={errors.reason?.message}
              />
            )}
          />
        </div>
      </DialogContent>

      <DialogActions className="px-3 pb-3">
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={mutation.isPending}
          startIcon={<BTNLoader isLoading={mutation.isPending} />}
        >
          Grant Add-on
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GrantAddonModal;
